<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';
import useNumberField from '@/composables/useNumberField';
import { lookupPostcode } from '@/api';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: openapi.SuburbOpenapi | null;
  size?: FieldSize;
  fetchItems?: (postCode: number) => Promise<openapi.SuburbOpenapi[]>;
  loading?: boolean;
  minSearchLength?: number;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  fetchItems: lookupPostcode,
  loading: false,
  minSearchLength: 2,
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);

const items = ref<openapi.SuburbOpenapi[]>([]);
const isFetching = ref(false);

function fetchWrapper(): void {
  const postCode = search.value;
  isFetching.value = true;
  void props
    .fetchItems(Number(postCode)) // TODO String -> Number for the entire component
    .then((fetchedItems) => {
      if (postCode !== search.value) return;
      items.value = fetchedItems;
    })
    .finally(() => {
      isFetching.value = false;
    });
}

const debounceFetchWrapper = useDebounceFn(fetchWrapper, 150);

function getSearchString(search?: string | null): string {
  return (search?.trim() || '').toUpperCase();
}

function getSearchStringFromValue(
  value?: openapi.SuburbOpenapi | null,
): string {
  let search = '';
  if (value?.postcode) search = value.postcode;
  return getSearchString(search);
}

watch(search, (newSearch?: string | null, oldSearch?: string | null) => {
  // console.debug('fieldValue.value', fieldValue.value);
  // console.debug('newSearch', newSearch);
  // console.debug('oldSearch', oldSearch);
  if (newSearch === null && fieldValue.value) {
    fieldValue.value = null;
    return;
  }
  newSearch = getSearchString(newSearch);
  oldSearch = getSearchString(oldSearch);
  const value = getSearchStringFromValue(fieldValue.value);
  if (newSearch.length >= props.minSearchLength) {
    if (newSearch !== oldSearch) {
      if (newSearch !== value || oldSearch !== '') {
        void debounceFetchWrapper();
      }
    }
    // } else if (newSearch === '' && oldSearch === value) {
  } else {
    items.value = [];
  }
});

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);
const { isError, handleKeypress } = useNumberField();

const sizeClass = computed(() => 'size-' + props.size);

// TODO use common logic with KNumberField
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    label="Postcode"
    type="number"
    min="0"
    max="9999"
    inputmode="numeric"
    step="1"
    :error="isError"
    :items="items"
    item-title="postcode"
    item-value="id"
    return-object
    :class="[
      'g-v-text-field',
      $style.postCodeAutocompleteField,
      $style[sizeClass],
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    clearable
    menu-icon="mdi-chevron-down"
    :loading="props.loading || isFetching"
    no-filter
    data-testid="postCodeAutocompleteField"
    @keydown.enter="handleEnterKeydown"
    @keypress="handleKeypress"
  >
    <template #no-data>
      <NoDataSelectVListItem>
        <template v-if="!search || search.length < props.minSearchLength">
          Please enter {{ props.minSearchLength }} or more characters
        </template>
        <template v-else>No such data</template>
      </NoDataSelectVListItem>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        v-bind="itemData.props"
      >
        <template #title>
          {{ itemData.item.raw.postcode }}, {{ itemData.item.raw.name }},
          {{ itemData.item.raw.state }}
        </template>
      </v-list-item>
    </template>
    <template #prepend="prependData">
      <template
        v-if="fieldValue && fieldValue.name && fieldValue.state && !field?.menu"
      >
        {{ fieldValue.name }}, {{ fieldValue.state }}
      </template>
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.postCodeAutocompleteField {
  position: relative;

  :global {
    .v-input__prepend {
      position: absolute;
      z-index: 1;
      left: 16px;
      font-size: 9px;
      line-height: 1;
      white-space: nowrap;
    }
    .v-autocomplete__selection {
      margin-top: 0;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.size-xs {
  :global {
    .v-input__prepend {
      top: 26px;
    }
  }
}

.size-sm {
  :global {
    .v-input__prepend {
      top: 30px;
    }
  }
}

.size-md {
  :global {
    .v-input__prepend {
      top: 30px;
    }
  }
}

.size-lg {
  :global {
    .v-input__prepend {
      top: 38px;
    }
  }
}
</style>
